import i18next from "i18next";
import { Fields } from "Shared/Form";
import DateSelector from "Shared/Form/DateSelector";
import DollarInput from "Shared/Form/Input/DollarInput";
import RadioGroup from "Shared/Form/RadioGroup";
import Select from "Shared/Form/Select";
import { currencyAmount, required } from "Shared/Form/validators";
import styled, { css } from "styled-components";
import CreditAppZipCode from "src/components/ContinueDeal/CreditApp/Residence/CreditAppZipCode";
import { ASTERISK } from "src/constants";
import { localizationDefaults } from "src/utils";
import { isUS } from "src/utils/account";
import { getStates } from "src/utils/states";

const TRANSLATION_KEY = "ContinueDeal.CreditApp.Residence";

const ColumnStyle = css`
    display: inline-block;
    padding: 10px;
    vertical-align: top;
`;

const ColumnFull = styled("div")`
    ${ColumnStyle};
    width: calc(100% - 20px);
`;

const ColumnMedium = styled("div")`
    ${ColumnStyle};
    width: calc(100% - 20px);

    ${(props) => props.theme.breakpoints.up("desktop")} {
        /* IE11 hack - without decimal it rounds pixels up and breaks layout */
        width: calc(100% / 3 * 2 - 30.1px / 3 * 2);
    }
`;

const ColumnSmallLarge = styled("div")`
    ${ColumnStyle};
    width: calc(100% - 20px);

    ${(props) => props.theme.breakpoints.up("desktop")} {
        /* IE11 hack - without decimal it rounds pixels up and breaks layout */
        width: calc(100% / 3 - 60.1px / 3);
    }
`;

const ColumnSmall = styled("div")`
    ${ColumnStyle};

    /* IE11 hack - without decimal it rounds pixels up and breaks layout */
    width: calc(100% / 3 - 60.1px / 3);

    ${(props) => props.theme.breakpoints.up("desktop")} {
        /* IE11 hack - without decimal it rounds pixels up and breaks layout */
        width: calc(100% / 3 - 60.1px / 3);
    }
`;

interface GetFieldsProps {
    region: string;
}

export function getFields({ region }: GetFieldsProps): Fields {
    return {
        address1: {
            label: `${i18next.t(`${TRANSLATION_KEY}.address1Label`)}*`,
            validators: [{ message: i18next.t(`${TRANSLATION_KEY}.addressRequiredMessage`), validator: required }],
            wrapper: ColumnFull,
        },
        address2: {
            label: i18next.t(`${TRANSLATION_KEY}.address2Label`),
            validators: [],
            wrapper: ColumnFull,
        },
        city: {
            label: `${i18next.t(`${TRANSLATION_KEY}.cityLabel`)}*`,
            validators: [required],
            wrapper: ColumnSmall,
        },
        monthlyPayment: {
            input: DollarInput,
            label: `${i18next.t(`${TRANSLATION_KEY}.monthlyPaymentLabel`)}*`,
            validators: [
                required,
                { message: i18next.t(`${TRANSLATION_KEY}.invalidRentAmountMessage`), validator: currencyAmount },
            ],
            wrapper: ColumnSmallLarge,
        },
        movingDate: {
            input: DateSelector,
            inputProps: {
                rangeEnd: new Date(),
                rangeStart: new Date(1930, 1, 1),
                type: "MY",
            },
            label: `${i18next.t(`${TRANSLATION_KEY}.movingDateLabel`)}${ASTERISK}`,
            validators: [required],
            wrapper: ColumnMedium,
        },
        owner: {
            initialValue: "yes",
            input: RadioGroup,
            inputProps: {
                options: [
                    { label: i18next.t(`${TRANSLATION_KEY}.ownHomeLabel`), value: "yes" },
                    { label: i18next.t(`${TRANSLATION_KEY}.rentHomeLabel`), value: "no" },
                ],
            },
            label: i18next.t(`${TRANSLATION_KEY}.ownerLabel`),
            validators: [],
            wrapper: ColumnFull,
        },
        state: {
            input: Select,
            inputProps: { options: getStates(region) },
            label: isUS(region)
                ? `${i18next.t(`${TRANSLATION_KEY}.stateLabel`)}*`
                : `${i18next.t(`${TRANSLATION_KEY}.provinceLabel`)}*`,
            validators: [required],
            wrapper: ColumnSmall,
        },
        zipCode: {
            input: CreditAppZipCode,
            inputProps: {
                location: {},
                name: "zipCode",
                region: localizationDefaults.country,
            },
            label: isUS(region)
                ? `${i18next.t(`${TRANSLATION_KEY}.zipCodeLabel`)}*`
                : `${i18next.t(`${TRANSLATION_KEY}.postalCodeLabel`)}*`,
            validators: [],
            wrapper: ColumnSmall,
        },
    };
}

interface GetExtraFieldsProps {
    region: string;
}

export function getExtraFields({ region }: GetExtraFieldsProps): Fields {
    return {
        extraAddress1: {
            label: `${i18next.t(`${TRANSLATION_KEY}.address1Label`)}*`,
            validators: [{ message: i18next.t(`${TRANSLATION_KEY}.addressRequiredMessage`), validator: required }],
            wrapper: ColumnFull,
        },
        extraAddress2: {
            label: i18next.t(`${TRANSLATION_KEY}.address2Label`),
            validators: [],
            wrapper: ColumnFull,
        },
        extraCity: {
            label: `${i18next.t(`${TRANSLATION_KEY}.cityLabel`)}*`,
            validators: [required],
            wrapper: ColumnSmall,
        },
        extraMonthlyPayment: {
            input: DollarInput,
            label: `${i18next.t(`${TRANSLATION_KEY}.monthlyPaymentLabel`)}*`,
            validators: [
                required,
                { message: i18next.t(`${TRANSLATION_KEY}.invalidRentAmountMessage`), validator: currencyAmount },
            ],
            wrapper: ColumnSmallLarge,
        },
        extraOwner: {
            initialValue: "yes",
            input: RadioGroup,
            inputProps: {
                options: [
                    { label: i18next.t(`${TRANSLATION_KEY}.ownHomeLabel`), value: "yes" },
                    { label: i18next.t(`${TRANSLATION_KEY}.rentHomeLabel`), value: "no" },
                ],
            },
            label: i18next.t(`${TRANSLATION_KEY}.ownerLabel`),
            validators: [],
            wrapper: ColumnFull,
        },
        extraState: {
            input: Select,
            inputProps: { options: getStates(region) },
            label: isUS(region)
                ? `${i18next.t(`${TRANSLATION_KEY}.stateLabel`)}*`
                : `${i18next.t(`${TRANSLATION_KEY}.provinceLabel`)}*`,
            validators: [required],
            wrapper: ColumnSmall,
        },
        extraZipCode: {
            input: CreditAppZipCode,
            inputProps: {
                location: {},
                name: "extraZipCode",
                region: localizationDefaults.country,
            },
            label: isUS(region)
                ? `${i18next.t(`${TRANSLATION_KEY}.zipCodeLabel`)}*`
                : `${i18next.t(`${TRANSLATION_KEY}.postalCodeLabel`)}*`,
            validators: [],
            wrapper: ColumnSmall,
        },
    };
}

export const fieldsPerRow = [
    ["address1"],
    ["address2"],
    ["city", "state", "zipCode"],
    ["owner"],
    ["monthlyPayment", "movingDate"],
];

export const extraFieldsPerRow = [
    ["extraAddress1"],
    ["extraAddress2"],
    ["extraCity", "extraState", "extraZipCode"],
    ["extraOwner"],
    ["extraMonthlyPayment"],
];
